/** Normalize a URL for comparison (lowercase host, strip default ports,
 * remove trailing slash on path, sort query params, drop common tracking params) */
export function normalizeUrlForCompare(input: string): string | null {
  try {
    const u = new URL(input);
    if (!['http:', 'https:'].includes(u.protocol)) return null;

    u.hostname = u.hostname.toLowerCase();

    // strip default ports
    if (
      (u.protocol === 'http:' && u.port === '80') ||
      (u.protocol === 'https:' && u.port === '443')
    ) {
      u.port = '';
    }

    // normalize path (remove trailing slash except for root)
    if (u.pathname !== '/' && u.pathname.endsWith('/')) {
      u.pathname = u.pathname.replace(/\/+$/, '');
    }

    // remove common tracking params and sort the rest
    const drop = new Set([
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
      'utm_id',
      'gclid',
      'fbclid',
      'mc_cid',
      'mc_eid',
    ]);
    const kept = new URLSearchParams();
    // sort keys
    const keys = Array.from(u.searchParams.keys()).sort();
    for (const k of keys) {
      if (!drop.has(k)) {
        const values = u.searchParams.getAll(k);
        for (const v of values) kept.append(k, v);
      }
    }
    u.search = kept.toString() ? `?${kept.toString()}` : '';

    // compare string
    return `${u.protocol}//${u.hostname}${u.port ? `:${u.port}` : ''}${u.pathname}${u.search}`;
  } catch {
    return null;
  }
}

/** Find index of duplicate of value at 'index' within the array (excluding itself). */
export function findDuplicateIndex(
  values: string[],
  index: number,
): number | null {
  // @ts-ignore
  const me = normalizeUrlForCompare(values[index]);
  if (!me) return null;
  for (let i = 0; i < values.length; i++) {
    if (i === index) continue;
    const other = values[i];
    if (!other) continue;
    const norm = normalizeUrlForCompare(other);
    if (norm && norm === me) return i;
  }
  return null;
}

/** Recompute duplicate errors for all fields (call after changes). */
export function computeDuplicateErrors(
  values: string[],
  baseErrors: (string | null)[],
): (string | null)[] {
  const errs = [...baseErrors];
  for (let i = 0; i < values.length; i++) {
    const v = values[i]?.trim();
    if (!v) {
      errs[i] = null;
      continue;
    } // blanks are fine
    // keep existing non-duplicate error if present (e.g., invalid URL)
    if (errs[i] && !errs[i]?.startsWith('Duplicate')) continue;
    const dup = findDuplicateIndex(values, i);
    errs[i] = dup !== null ? `Duplicate of Source #${dup + 1}` : null;
  }
  return errs;
}
